import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS, ANIM, GRADIENTS, BAR_COLORS } from '../../../../theme/tokens';

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

/* Balance banner — the owner's current credits, on the Lumio identity wash. */
export const Balance = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 14px;
  background: ${GRADIENTS.featureCard};
  border: 1px solid ${COLORS.hairline};
`;

export const BalanceMark = styled.img`
  width: 22px;
  height: 22px;
  display: block;
`;

export const BalanceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

export const BalanceAmount = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.heading};
  font-variant-numeric: tabular-nums;
`;

export const BalanceLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

export const Explain = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 13px;
  line-height: 19px;
  color: ${COLORS.textMuted};
`;

export const SectionLabel = styled(Text).attrs({ tag: 'h4' as const })`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
  margin-bottom: 10px;
`;

/* Top-up: pack chips + a custom amount. */
export const PackRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

export const PackCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  padding: 11px 12px;
  border-radius: 12px;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? COLORS.ctaTint : COLORS.surfaceFaint)};
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.ctaSolid : COLORS.hairline)};
  box-shadow: ${({ $selected }) => ($selected ? `0 6px 20px -12px ${COLORS.ctaSolid}` : 'none')};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE}, background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? COLORS.ctaSolid : COLORS.hoverBorderLit)};
  }
`;

/* Number + unit on one line, vertically CENTERED (not baseline — the Lumio mark is a graphic with no
   text baseline, so baseline alignment floats it high). Wraps as a whole ("500" over "AI credits") in
   a tight card instead of breaking the phrase mid-word. */
export const PackTop = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

export const PackCredits = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  color: ${COLORS.text};
  font-variant-numeric: tabular-nums;
`;

/* The "AI credits" unit: the Lumio outline mark + the label, kept together (nowrap) so the pair wraps
   as one under the number in a tight card. */
export const PackUnitRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`;

/** The Lumio outline spark prefixing the unit, sized to the label so icon and text read as one glyph. */
export const PackUnitIcon = styled.img`
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
`;

/** The "AI credits" label — quieter than the number, but legible (13px, muted, not faint). */
export const PackUnit = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  color: ${COLORS.textMuted};
`;

/* Same font treatment as the custom field's live USD (FieldUsd) so the price a suggestion costs is
   just as legible as the one you type — 13px semibold, muted (not faint), tabular figures. */
export const PackPrice = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  font-variant-numeric: tabular-nums;
`;

/* Unified custom-amount field: the input, its unit, and the live USD cost all sit inside one bordered
   control, so the money reads as part of the field rather than a figure floating beside it. Focus lights
   the border orange, matching the selected pack + the CTA. */
export const Field = styled.div<{ $focused: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 14px;
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${({ $focused }) => ($focused ? COLORS.ctaSolid : COLORS.hairline)};
  box-shadow: ${({ $focused }) => ($focused ? `0 6px 20px -12px ${COLORS.ctaSolid}` : 'none')};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE}, box-shadow ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

export const FieldInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  &::placeholder {
    color: ${COLORS.textFaint};
    font-weight: 400;
  }
  &:focus {
    outline: none;
  }
`;

export const FieldUnit = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  font-size: 13px;
  color: ${COLORS.textFaint};
`;

/** The live money cost, inside the field behind a hairline divider — reads as part of the control. */
export const FieldUsd = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  padding-left: 12px;
  border-left: 1px solid ${COLORS.hairline};
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  font-variant-numeric: tabular-nums;
`;

/* Payment method — an explicit radio choice (wallet vs card) in place of two footer buttons. */
export const PayGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PayOption = styled.button<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.55 : 1)};
  background: ${({ $selected }) => ($selected ? COLORS.ctaTint : COLORS.surfaceFaint)};
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.ctaSolid : COLORS.hairline)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE}, background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $selected, $disabled }) =>
      $disabled ? COLORS.hairline : $selected ? COLORS.ctaSolid : COLORS.hoverBorderLit};
  }
`;

/** Custom radio dot: an orange ring + a scaling orange centre when chosen. */
export const RadioDot = styled.span<{ $on: boolean }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ $on }) => ($on ? COLORS.ctaSolid : COLORS.hoverBorderLit)};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${COLORS.ctaSolid};
    transform: scale(${({ $on }) => ($on ? 1 : 0)});
    transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
`;

export const PayText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PayLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const PaySub = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
  font-variant-numeric: tabular-nums;
`;

/** A subtle funding / status note under the amount row (MAIN can't cover this, a deposit is settling…). */
export const FundingNote = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.textMuted};
`;

/** Inline error row (a failed purchase). */
export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ErrorText = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${BAR_COLORS.danger};
`;
